import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getCensusPayor,
  censusPayorAction,
  censusGroupPlanAction,
  censusBillingRules,
} from "../services/soar";
import GroupPlanModal from "./GroupPlanModal";
import FloatingInput from "../common/FloatingInput";
import ExpandDiv from "../common/ExpandDiv";
import Checkbox from "../common/Checkbox";

function RevPayor(props) {
  const { payorId } = useParams();
  const [payor, setPayor] = useState({});
  const [groupPlans, setGroupPlans] = useState([]);
  const [history, setHistory] = useState([]);
  const [billingRules, setBillingRules] = useState([]);
  const [showExpired, setShowExpired] = useState(false);
  const navigate = useNavigate();
  const applicationId =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
      : "58990f83-8566-4ba8-b4b5-584a64e4cba1";

  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const writeAccess = !(
    safyInfo.user.modules.includes("Admin") ||
    safyInfo.user.modules.includes("Rev Cycle")
  );

  useEffect(() => {
    loadPayor();
    loadGroupPlans();
    loadHistory();
    loadBillingRules();
  }, []);

  const loadPayor = async () => {
    const response = await getCensusPayor(payorId);
    setPayor(response);
  };
  const loadGroupPlans = async () => {
    const response = await censusGroupPlanAction({
      command: "query",
      keyword: "bypayor",
      application: applicationId,
      ids: [payorId],
    });
    setGroupPlans(response.data);
  };
  const loadHistory = async () => {
    const response = await censusPayorAction({
      command: "query",
      keyword: "history",
      ids: [payorId],
      application: applicationId,
    });
    if (response.code === "success" && response.data) {
      setHistory(
        response.data.sort(
          (a, b) => new Date(a.validFrom) - new Date(b.validFrom)
        )
      );
    } else {
      setHistory([]);
    }
  };

  const loadBillingRules = async () => {
    const response = await censusBillingRules({
      command: "query",
      application: applicationId,
      keyword: "activerulesforpayor",
      searchTerms: {
        payor: payorId,
      },
    });
    setBillingRules(response.data);
  };

  const handleChange = ({ currentTarget: input }) => {
    const value =
      input.type === "checkbox" ? input.checked.toString() : input.value;
    const payorData = { ...payor };
    payorData[input.name] = input.name === "name" ? value.toUpperCase() : value;
    setPayor(payorData);
  };

  const updatePayor = async () => {
    const response = await censusPayorAction({
      command: "update",
      application: applicationId,
      id: payorId,
      name: payor.name,
      code: payor.code,
      type: payor.type,
      street1: payor.street1,
      street2: payor.street2,
      city: payor.city,
      state: payor.state,
      zip: payor.zip,
      contact: payor.contact,
      phone: payor.phone,
      mobile: payor.mobile,
      email: payor.email,
      effective: payor.effective,
      expiration: payor.expiration,
    });
    if (response.code && response.code === "success") {
      toast.success("Payor updated");
    } else {
      toast.error(`Error: ${response.message}`);
    }
  };

  const removePayor = async () => {
    const response = await censusPayorAction({
      command: "deactivate",
      application:
        process.env.REACT_APP_ENVIRONMENT === "Production"
          ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
          : "58990f83-8566-4ba8-b4b5-584a64e4cba1",
      id: payorId,
    });

    if (response.code === "success") {
      toast.info("Payor Removed");
      navigate("/rev/payors");
    } else {
      toast.error(`Error Removing Payor. ${response.message}`);
    }
  };

  function diffObjects(obj1, obj2) {
    const differences = {};

    // Check keys in obj1
    for (const key in obj1) {
      // console.log("Test Obj 2 for key: ", key);
      if (!obj2.hasOwnProperty(key)) {
        // console.log("Obj 2 does not have key: ", key);
        differences[key] = { obj1: obj1[key], obj2: undefined };
      } else if (
        typeof obj1[key] === "object" &&
        typeof obj2[key] === "object"
      ) {
        const nestedDiff = diffObjects(obj1[key], obj2[key]);
        if (Object.keys(nestedDiff).length > 0) {
          // console.log("Nested Diff: ", nestedDiff);
          differences[key] = nestedDiff;
        }
      } else if (obj1[key] !== obj2[key]) {
        // console.log("Obj 1 and Obj 2 are different for key: ", key);
        differences[key] = { obj1: obj1[key], obj2: obj2[key] };
      }
    }

    // Check keys in obj2 that are not in obj1
    for (const key in obj2) {
      // console.log("Test Obj 1 for key: ", key);
      if (!obj1.hasOwnProperty(key)) {
        // console.log("Obj 1 does not have key: ", key);
        differences[key] = { obj1: undefined, obj2: obj2[key] };
      }
    }

    // console.log("Differences: ", differences);
    return differences;
  }

  const [GroupModalOpen, setGroupModalOpen] = useState(false);
  const [groupPlan, setGroupPlan] = useState({
    name: "",
    application: applicationId,
    payor: payorId,
    effective: "",
    expiration: "",
  });
  const closeGroupModal = () => {
    setGroupModalOpen(false);
  };

  return (
    <div>
      <GroupPlanModal
        isOpen={GroupModalOpen}
        handleClose={closeGroupModal}
        groupPlan={groupPlan}
        setGroupPlan={setGroupPlan}
        updateGroupPlans={loadGroupPlans}
        payor={payorId}
      />
      <div className="mb-2">
        <div className="row mb-2">
          <div className="col-10"></div>
          <div className="col-2 text-end">
            {billingRules.length === 0 && (
              <i
                className="fas fa-trash-alt fa-lg text-danger"
                role="button"
                onClick={() => removePayor()}
              ></i>
            )}
          </div>
        </div>
        <div className="row rounded border border-dark bg-safy2-1 py-2 ">
          <div className="col-4">
            <FloatingInput
              name="name"
              label="Payor Name"
              value={payor.name}
              onChange={handleChange}
              disabled={writeAccess}
            />
          </div>
          <div className="col-2">
            <FloatingInput
              name="code"
              label="Payor Code"
              value={payor.code}
              onChange={handleChange}
              type="select"
              options={[
                { _id: "AL", name: "AL" },
                { _id: "CO", name: "CO" },
                { _id: "IN", name: "IN" },
                { _id: "KY", name: "KY" },
                { _id: "NV", name: "NV" },
                { _id: "OH", name: "OH" },
                { _id: "SC", name: "SC" },
              ]}
              disabled={writeAccess}
            />
          </div>
          <div className="col-2">
            <FloatingInput
              name="type"
              label="Type"
              value={payor.type}
              onChange={handleChange}
              type="select"
              options={[
                { _id: "Group", name: "Group" },
                { _id: "Federal", name: "Federal" },
              ]}
              disabled={writeAccess}
            />
          </div>
          <div className="col-2">
            <FloatingInput
              name="effective"
              label="Effective"
              value={payor.effective}
              onChange={handleChange}
              type="date"
              disabled={writeAccess}
            />
          </div>
          <div className="col-2">
            <FloatingInput
              name="expiration"
              label="Expiration"
              value={payor.expiration}
              onChange={handleChange}
              type="date"
              disabled={writeAccess}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-6 p-2">
          <div className="rounded border border-dark bg-info p-2">
            <div className="h5">Address</div>
            <FloatingInput
              name="street1"
              label="Street 1"
              value={payor.street1}
              onChange={handleChange}
              disabled={writeAccess}
            />
            <FloatingInput
              name="street2"
              label="Street 2"
              value={payor.street2}
              onChange={handleChange}
              disabled={writeAccess}
            />
            <FloatingInput
              name="city"
              label="City"
              value={payor.city}
              onChange={handleChange}
              disabled={writeAccess}
            />
            <div className="row">
              <div className="col-6">
                <FloatingInput
                  name="state"
                  label="State"
                  value={payor.state}
                  onChange={handleChange}
                  disabled={writeAccess}
                />
              </div>
              <div className="col-6">
                <FloatingInput
                  name="zip"
                  label="Zip"
                  value={payor.zip}
                  onChange={handleChange}
                  disabled={writeAccess}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 p-2">
          <div className="rounded border border-dark bg-info p-2">
            <div className="h5">Info</div>
            <FloatingInput
              name="contact"
              label="Contact"
              value={payor.contact}
              onChange={handleChange}
              disabled={writeAccess}
            />
            <FloatingInput
              name="phone"
              label="Phone"
              value={payor.phone}
              onChange={handleChange}
              disabled={writeAccess}
            />
            <FloatingInput
              name="mobile"
              label="Mobile"
              value={payor.mobile}
              onChange={handleChange}
              disabled={writeAccess}
            />
            <FloatingInput
              name="email"
              label="Email"
              value={payor.email}
              onChange={handleChange}
              disabled={writeAccess}
            />
          </div>
        </div>
      </div>
      {!writeAccess && (
        <div className="btn btn-success" onClick={() => updatePayor()}>
          Update Payor
        </div>
      )}
      <div className="border rounded my-4 p-2">
        <div className="row">
          <div className="col-6 h5">Group Plans</div>
          <div className="col-5">
            <Checkbox
              name="Active"
              label="Show Expired"
              onChange={() => setShowExpired(!showExpired)}
              checked={showExpired}
            />
          </div>
          <div className="col-1">
            {!writeAccess && (
              <i
                className="fas fa-plus-circle fa-lg text-primary"
                role="button"
                onClick={() => {
                  setGroupPlan({
                    name: "",
                    application: applicationId,
                    payor: payorId,
                    effective: "",
                  });
                  setGroupModalOpen(true);
                }}
              ></i>
            )}
          </div>
        </div>
        {groupPlans
          .filter((gp) =>
            showExpired === true
              ? gp
              : gp.expiration == null || gp.expiration === ""
          )
          .map((gp) => (
            <div
              key={gp.id}
              className={`rounded border ${
                gp.expiration && gp.expiration !== ""
                  ? "bg-danger"
                  : "bg-secondary"
              } text-white m-2 p-2`}
            >
              <div>
                <div className="row">
                  <div className="col-11 fw-bold">{gp.name}</div>
                  <div className="col-1 fw-light">
                    {(gp.expiration == null || gp.expiration === "") && (
                      <i
                        className="fas fa-pen text-white float-end"
                        role="button"
                        onClick={() => {
                          const obj = {
                            id: gp.id,
                            name: gp.name,
                            application: applicationId,
                            payor: payorId,
                            effective: gp.effective,
                            expiration: gp.expiration,
                          };
                          setGroupPlan(obj);
                          setGroupModalOpen(true);
                        }}
                      />
                    )}
                  </div>
                </div>
                <div>{`Effective: ${new Date(
                  gp.effective
                ).toLocaleDateString()}`}</div>
                {gp.expiration && (
                  <div>{`Expiration: ${new Date(
                    gp.expiration
                  ).toLocaleDateString()}`}</div>
                )}
              </div>
              {billingRules
                .filter((br) => br.groupPlanId === gp.id)
                .map((gpbr, index) => (
                  <div
                    key={`gpbr${gpbr.id}-${index}`}
                    className="row rounded border border-dark bg-primary m-2 p-2"
                  >
                    <div className="col">Billing Rule:</div>
                    <div className="col">{gpbr.serviceConfigurationName}</div>
                    <div className="col">{`Effective: ${gpbr.effective}`}</div>
                    <div className="col">
                      <i
                        className="fas fa-circle-arrow-right fa-xl float-end mt-2 pt-1"
                        role="button"
                        onClick={() => navigate(`/rev/rules/${gpbr.id}`)}
                      ></i>
                    </div>
                  </div>
                ))}
            </div>
          ))}
      </div>

      {history && history.length > 1 ? (
        <>
          <div className="border rounded my-4 p-2">
            <div className="h5">Payor History</div>
            <div className="accordion" id="payorHistoryAccordion">
              {history.map((histItem, index) => (
                <div key={`hist-${index}`} className="card mb-2">
                  <div className="card-header bg-safy-info text-white">
                    <div className="row">
                      <div className="col-6">
                        <strong>Modified On:</strong>{" "}
                        {new Date(histItem.modifiedOn).toLocaleString()}
                      </div>
                      <div className="col-6">
                        <strong>Modified By:</strong> {histItem.modifiedByName}
                      </div>
                    </div>
                    {histItem.validFrom && (
                      <div className="row mt-1">
                        <div className="col-6">
                          <strong>Valid From:</strong>{" "}
                          {new Date(histItem.validFrom).toLocaleString()}
                        </div>
                        <div className="col-6">
                          <strong>Valid To:</strong>{" "}
                          {histItem.validTo
                            ? new Date(histItem.validTo).toLocaleString()
                            : "Current"}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="card-body">
                    {index === 0 ? (
                      <div className="row">
                        <div className="col-12">
                          <div className="d-flex justify-content-between">
                            <div className="rounded border border-dark bg-success text-white px-2 ">
                              Original Values:
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-4">
                              <strong>Name:</strong> {histItem.name}
                            </div>
                            <div className="col-4">
                              <strong>Code:</strong> {histItem.code}
                            </div>
                            <div className="col-4">
                              <strong>Type:</strong> {histItem.type}
                            </div>
                          </div>
                          <div>
                            <strong>Street 1: </strong>
                            {histItem.street1}
                          </div>
                          <div>
                            <strong>Street 2: </strong>
                            {histItem.street2}
                          </div>
                          <div className="row">
                            <div className="col-4">
                              <strong>City:</strong> {histItem.city}
                            </div>
                            <div className="col-4">
                              <strong>State:</strong> {histItem.state}
                            </div>
                            <div className="col-4">
                              <strong>Zip:</strong> {histItem.zip}
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-4">
                              <strong>Contact:</strong> {histItem.contact}
                            </div>
                            <div className="col-4">
                              <strong>Phone:</strong> {histItem.phone}
                            </div>
                            <div className="col-4">
                              <strong>Email:</strong> {histItem.email}
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-6">
                              <strong>Effective:</strong> {histItem.effective}
                            </div>
                            <div className="col-6">
                              <strong>Expiration:</strong> {histItem.expiration}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="row">
                        <div className="col-12">
                          <div className="d-flex justify-content-between">
                            <div className="rounded border border-dark bg-primary text-white px-2 ">
                              Changes:
                            </div>
                          </div>
                          {Object.entries(
                            diffObjects(history[index - 1], histItem)
                          )
                            .filter(
                              ([key]) =>
                                ![
                                  "id",
                                  "modifiedById",
                                  "modifiedByName",
                                  "validFrom",
                                  "validTo",
                                ].includes(key)
                            )
                            .map(([key, diff], idx) => (
                              <div
                                key={`diff-${index}-${idx}`}
                                className="row mb-1"
                              >
                                <div className="col-3 fw-bold text-capitalize">
                                  {key}:
                                </div>
                                <div className="col-4 text-danger">
                                  {diff.obj1 || "Empty"}
                                </div>
                                <div className="col-1">
                                  <i className="fas fa-arrow-right"></i>
                                </div>
                                <div className="col-4 text-success">
                                  {diff.obj2 || "Empty"}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="row">
          <div className="col">{`Created On: ${payor.createdOn}`}</div>
          <div className="col">{`Created By: ${payor.createdByName}`}</div>
        </div>
      )}
    </div>
  );
}

export default RevPayor;
